import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { Spinner } from '../ui/spinner';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../ui/table';
import { useBrand } from '../../context/BrandContext';
import { gameAnalysisAPI } from '../../services/api';
import { ChannelBadge, SourcedValue } from './Provenance';
import ShotGallery from './ShotGallery';
import {
  ISSUE_LEVELS, bonusBuyRows, currencyRows, facetRows, factRows, formatCompleteness, formatDate, formatNumber,
  formatPct, humanize, issueCounts, safeHttpUrl, sortIssues, symbolRows,
} from './analysisData';

function Section({ title, description, children, className = '' }) {
  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
        {description && <CardDescription className="text-xs">{description}</CardDescription>}
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

function KeyFacts({ summary, factsCompleteness }) {
  const rows = factRows(summary);
  if (rows.length === 0) return <p className="text-sm text-ink-dim">No facts assembled.</p>;
  return (
    <>
      {factsCompleteness?.missing?.length > 0 && (
        <p className="text-xs text-ink-dim mb-3">
          Not found: {factsCompleteness.missing.map(humanize).join(', ')}
        </p>
      )}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Fact</TableHead>
            <TableHead>Value</TableHead>
            <TableHead>Source</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map(r => (
            <TableRow key={r.key}>
              <TableCell className="text-ink-dim whitespace-nowrap">{r.label}</TableCell>
              <TableCell className="font-bold text-ink">
                {r.text}
                {r.conflict && <p className="text-[11px] font-medium text-warning">{r.conflict}</p>}
              </TableCell>
              <TableCell>
                <div className="flex flex-wrap items-center gap-2">
                  <ChannelBadge {...r} />
                  <span className="text-[11px] text-ink-mute">{r.source}</span>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </>
  );
}

function Ladder({ steps }) {
  return (
    <div className="flex flex-wrap gap-1 py-1">
      {steps.map((s, i) => <Badge key={i} variant="outline" className="font-medium">{s}</Badge>)}
    </div>
  );
}

function Bets({ currencies }) {
  const { rows, notCaptured } = useMemo(() => currencyRows(currencies), [currencies]);
  const [expanded, setExpanded] = useState(null);
  if (rows.length === 0) return <p className="text-sm text-ink-dim">No bet configuration captured.</p>;
  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-6" />
            <TableHead>Currency</TableHead>
            <TableHead className="text-right">Min</TableHead>
            <TableHead className="text-right">Max</TableHead>
            <TableHead className="text-right">Steps</TableHead>
            <TableHead>Compared with EUR</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map(r => {
            const isOpen = expanded === r.currency;
            const canOpen = r.ladder.length > 0;
            return (
              <Fragment key={r.currency}>
                <TableRow
                  onClick={() => canOpen && setExpanded(isOpen ? null : r.currency)}
                  className={canOpen ? 'cursor-pointer' : ''}
                >
                  <TableCell className="text-ink-mute">
                    {canOpen && (isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />)}
                  </TableCell>
                  <TableCell className="font-bold text-ink">{r.currency}</TableCell>
                  {r.status === 'captured' ? (
                    <>
                      <TableCell className="text-right">{r.min}</TableCell>
                      <TableCell className="text-right">{r.max}</TableCell>
                      <TableCell className="text-right">{r.steps ?? '—'}</TableCell>
                      <TableCell className="text-ink-dim">{r.vsEUR || (r.currency === 'EUR' ? 'reference' : '—')}</TableCell>
                    </>
                  ) : (
                    <TableCell colSpan={4} className="text-negative">Not captured{r.note ? ` — ${r.note}` : ''}</TableCell>
                  )}
                </TableRow>
                {isOpen && (
                  <TableRow className="hover:bg-transparent">
                    <TableCell />
                    <TableCell colSpan={5}><Ladder steps={r.ladder} /></TableCell>
                  </TableRow>
                )}
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
      {notCaptured.length > 0 && (
        <p className="text-[11px] text-ink-mute mt-2">Not captured (crypto): {notCaptured.join(', ')}</p>
      )}
    </>
  );
}

function BonusBuys({ bonusBuy }) {
  const rows = bonusBuyRows(bonusBuy);
  const [expanded, setExpanded] = useState(null);
  if (rows.length === 0) return <p className="text-sm text-ink-dim">No bonus buy or ante bet found.</p>;
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-6" />
          <TableHead>Feature</TableHead>
          <TableHead>Cost</TableHead>
          <TableHead>Price (EUR)</TableHead>
          <TableHead>RTP</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map(b => {
          const ladder = b.priceRange?.ladder || [];
          const isOpen = expanded === b.id;
          return (
            <Fragment key={b.id}>
              <TableRow
                onClick={() => ladder.length && setExpanded(isOpen ? null : b.id)}
                className={ladder.length ? 'cursor-pointer' : ''}
              >
                <TableCell className="text-ink-mute">
                  {ladder.length > 0 && (isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />)}
                </TableCell>
                <TableCell>
                  <p className="font-bold text-ink">{b.name}</p>
                  {b.aliases.length > 0 && <p className="text-[11px] text-ink-dim">also: {b.aliases.join(', ')}</p>}
                  <p className="text-[11px] text-ink-mute">{b.kind}{b.available === false ? ' · not available in this session' : ''}</p>
                  {b.description && <p className="text-[11px] text-ink-dim max-w-md">{b.description}</p>}
                </TableCell>
                <TableCell><SourcedValue value={b.cost} /></TableCell>
                <TableCell><SourcedValue value={b.price || b.priceRange} /></TableCell>
                <TableCell><SourcedValue value={b.rtp} /></TableCell>
              </TableRow>
              {isOpen && (
                <TableRow className="hover:bg-transparent">
                  <TableCell />
                  <TableCell colSpan={4}>
                    <Ladder steps={ladder.map(s => `${s.bet} → ${s.price}`)} />
                    {b.priceRange.detail && <p className="text-[11px] text-ink-mute">{b.priceRange.detail}</p>}
                  </TableCell>
                </TableRow>
              )}
            </Fragment>
          );
        })}
      </TableBody>
    </Table>
  );
}

function Paytable({ profile }) {
  const symbols = symbolRows(profile?.symbols_x);
  const pt = profile?.paytable || {};
  const features = pt.features || [];
  const fallbackSymbols = symbols.length === 0 ? (pt.symbols || []) : [];
  return (
    <div className="space-y-5">
      {symbols.length > 0 && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Symbol</TableHead>
              <TableHead>Pays (× bet)</TableHead>
              <TableHead>Source</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {symbols.map((s, i) => (
              <TableRow key={`${s.name}-${i}`}>
                <TableCell className="font-bold text-ink">{s.name}</TableCell>
                <TableCell>{s.pays}</TableCell>
                <TableCell><ChannelBadge {...s} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {fallbackSymbols.length > 0 && (
        <ul className="space-y-1 text-sm">
          {fallbackSymbols.map((s, i) => (
            <li key={i}><span className="font-bold text-ink">{s.name}</span>{s.description && <span className="text-ink-dim"> — {s.description}</span>}</li>
          ))}
        </ul>
      )}
      {features.length > 0 && (
        <div>
          <p className="text-[11px] font-bold uppercase tracking-wide text-ink-mute mb-2">Features (from the rules)</p>
          <ul className="space-y-2 text-sm">
            {features.map((f, i) => (
              <li key={i}><span className="font-bold text-ink">{f.name}</span>{f.description && <span className="text-ink-dim"> — {f.description}</span>}</li>
            ))}
          </ul>
        </div>
      )}
      {symbols.length === 0 && fallbackSymbols.length === 0 && features.length === 0 && (
        <p className="text-sm text-ink-dim">Nothing extracted from the paytable.</p>
      )}
    </div>
  );
}

function Classification({ c }) {
  if (!c) return <p className="text-sm text-ink-dim">Not classified yet.</p>;
  const rows = [
    { label: 'type', text: [c.primary_type, ...(c.types || []).filter(t => t !== c.primary_type)].filter(Boolean).map(humanize).join(', ') },
    { label: 'themes', text: (c.themes || []).map(humanize).join(', ') },
    { label: 'theme groups', text: (c.theme_groups || []).map(humanize).join(', ') },
    { label: 'archetypes', text: (c.archetypes || []).map(humanize).join(', ') },
    ...facetRows(c.facets),
    { label: 'RTP band', text: c.rtp_band ? humanize(c.rtp_band) : '' },
    { label: 'volatility', text: c.volatility ? `${humanize(c.volatility)}${c.volatility_selectable ? ' (selectable)' : ''}` : '' },
    { label: 'branded', text: c.branded ? (c.brand || 'yes') : '' },
    { label: 'provably fair', text: c.provably_fair ? 'yes' : '' },
  ].filter(r => r.text);
  return (
    <>
      <dl className="grid grid-cols-[140px_1fr] gap-x-4 gap-y-1.5 text-sm">
        {rows.map(r => (
          <Fragment key={r.label}>
            <dt className="text-ink-dim">{r.label}</dt>
            <dd className="text-ink">{r.text}</dd>
          </Fragment>
        ))}
      </dl>
      {c.catalog_disagreement && <p className="text-[11px] text-warning mt-3">Catalog disagrees: {c.catalog_disagreement}</p>}
      <p className="text-[11px] text-ink-mute mt-3">Taxonomy {c.taxonomy_version} · classified {formatDate(c.classified_at)}</p>
    </>
  );
}

function Similar({ similar }) {
  if (!similar?.length) return <p className="text-sm text-ink-dim">No similar analysed games yet.</p>;
  return (
    <ul className="space-y-2">
      {similar.map(s => (
        <li key={s.slug} className="flex items-baseline justify-between gap-3">
          <Link to={`/game-analysis/${encodeURIComponent(s.slug)}`} className="font-bold text-ink hover:text-cta">{s.title}</Link>
          <span className="text-[11px] text-ink-mute whitespace-nowrap" title={s.shared.map(humanize).join(', ')}>
            {Math.round(s.score * 100)}% · {s.shared.length} shared
          </span>
        </li>
      ))}
    </ul>
  );
}

function Issues({ issues }) {
  const sorted = sortIssues(issues);
  if (sorted.length === 0) return <p className="text-sm text-ink-dim">No issues — the capture is complete.</p>;
  return (
    <ul className="space-y-2">
      {sorted.map((i, n) => {
        const level = ISSUE_LEVELS[i.level] || ISSUE_LEVELS.info;
        return (
          <li key={n} className="flex items-start gap-3">
            <Badge variant={level.variant} className="shrink-0">{level.label}</Badge>
            <div>
              <p className="text-sm font-bold text-ink">{i.kind} <span className="text-[11px] font-medium text-ink-mute">· {i.where}</span></p>
              <p className="text-xs text-ink-dim whitespace-pre-line break-words">{i.text}</p>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function External({ facts }) {
  if (!facts?.length) return null;
  return (
    <Section title="External sources" description="Values the game team entered from provider client areas or game sheets.">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Field</TableHead>
            <TableHead>Value</TableHead>
            <TableHead>Source</TableHead>
            <TableHead>Verified</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {facts.map((f, i) => (
            <TableRow key={i}>
              <TableCell className="text-ink-dim">{humanize(f.field)}</TableCell>
              <TableCell className="font-bold text-ink">
                {typeof f.value === 'object' ? JSON.stringify(f.value) : String(f.value)}
                {f.note && <p className="text-[11px] font-medium text-ink-mute">{f.note}</p>}
              </TableCell>
              <TableCell className="text-ink-dim">{safeHttpUrl(f.url) ? <a href={safeHttpUrl(f.url)} target="_blank" rel="noreferrer" className="hover:text-cta">{f.source}</a> : f.source}</TableCell>
              <TableCell className="whitespace-nowrap text-ink-dim">{formatDate(f.verifiedAt)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Section>
  );
}

function CatalogAndTech({ game, profile }) {
  const cat = game.catalog || {};
  const tech = profile?.tech || {};
  const rows = [
    ['catalog RTP', cat.rtp != null ? formatPct(cat.rtp) : ''],
    ['catalog volatility', cat.volatility ? humanize(cat.volatility) : ''],
    ['catalog hit rate', cat.hit_rate != null ? formatPct(cat.hit_rate) : ''],
    ['lines / ways', [cat.lines && `${cat.lines} lines`, cat.ways && `${cat.ways} ways`].filter(Boolean).join(' · ')],
    ['devices', (cat.devices || []).join(', ')],
    ['categories', (cat.categories || []).map(humanize).join(', ')],
    ['session', tech.mode ? humanize(tech.mode) : ''],
    ['game host', tech.frame_host || ''],
    ['frameworks', (tech.frameworks || []).join(', ')],
    ['analysed', profile?.assembled_at ? `${formatDate(profile.assembled_at)}${profile.prompt_version ? ` · prompts ${profile.prompt_version}` : ''}` : ''],
    ['model cost', profile?.llm_cost_usd != null ? `$${formatNumber(profile.llm_cost_usd, 3)}` : ''],
  ].filter(([, v]) => v);
  return (
    <dl className="grid grid-cols-[140px_1fr] gap-x-4 gap-y-1.5 text-sm">
      {rows.map(([k, v]) => (
        <Fragment key={k}>
          <dt className="text-ink-dim">{k}</dt>
          <dd className="text-ink break-words">{v}</dd>
        </Fragment>
      ))}
    </dl>
  );
}

export default function GameAnalysisGamePage() {
  const { slug } = useParams();
  const { activeBrand } = useBrand();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    gameAnalysisAPI.getGame(slug)
      .then(res => { if (!cancelled) setData(res.data); })
      .catch(err => {
        if (cancelled) return;
        setData(null);
        setError(err.response?.status === 404 ? 'This game has not been analysed.' : (err.response?.data?.error || 'Could not load the game.'));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug, activeBrand]);

  const back = (
    <Link to="/game-analysis" className="inline-flex items-center gap-1 text-xs font-medium text-ink-dim hover:text-ink mb-3">
      <ArrowLeft className="h-3.5 w-3.5" /> All analysed games
    </Link>
  );

  if (loading) {
    return <div className="max-w-[1400px] mx-auto">{back}<div className="flex justify-center py-16"><Spinner /></div></div>;
  }
  if (error || !data) {
    return (
      <div className="max-w-[1400px] mx-auto">
        {back}
        <Alert variant="error"><AlertDescription>{error || 'Could not load the game.'}</AlertDescription></Alert>
      </div>
    );
  }

  const { game, profile } = data;
  const counts = issueCounts(data.issues);
  const facts = profile?.facts_completeness;

  return (
    <div className="max-w-[1400px] mx-auto space-y-4">
      <div>
        {back}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="font-display text-2xl font-black text-ink">{game.title}</h2>
            <p className="text-sm text-ink-dim">{game.provider}{game.identifier ? ` · ${game.identifier}` : ''}</p>
            <div className="flex flex-wrap gap-2 mt-2">
              <Badge variant={game.status === 'done' ? 'success' : game.status === 'failed' ? 'destructive' : 'secondary'}>{game.status}</Badge>
              {data.classification?.primary_type && <Badge variant="secondary">{humanize(data.classification.primary_type)}</Badge>}
              {counts.bad > 0 && <Badge variant="destructive">{counts.bad} failed</Badge>}
              {counts.warn > 0 && <Badge variant="warning">{counts.warn} warnings</Badge>}
            </div>
          </div>
          <div className="flex flex-col items-end gap-1 text-sm">
            <div className="flex gap-4">
              <div className="text-right">
                <p className="text-[11px] text-ink-mute">Key facts</p>
                <p className="font-bold text-ink">{facts ? `${facts.have}/${facts.of}` : '—'}</p>
              </div>
              <div className="text-right">
                <p className="text-[11px] text-ink-mute">Capture</p>
                <p className="font-bold text-ink">{formatCompleteness(profile?.completeness)}</p>
              </div>
            </div>
            <div className="flex gap-3 mt-1">
              {safeHttpUrl(game.links?.real) && (
                <a href={safeHttpUrl(game.links.real)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-medium text-ink-dim hover:text-cta">
                  Real-money game <ExternalLink className="h-3 w-3" />
                </a>
              )}
              <a href={safeHttpUrl(game.links?.site) || undefined} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-medium text-ink-dim hover:text-cta">
                Bitstarz page <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </div>
        </div>
      </div>

      <Section
        title="What went wrong"
        description={`Failed or skipped steps of the analysis and what the capture may have missed. Re-run only the failed steps: make game-analysis.retry ONLY=${game.slug} (bi-services).`}
      >
        <Issues issues={data.issues} />
      </Section>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Section title="Key facts" description="Hover a source for its detail and what other sources claim.">
          <KeyFacts summary={profile?.summary} factsCompleteness={facts} />
        </Section>
        <Section title="Bets per currency" description="Real-money bet ladders; click a currency for every step.">
          <Bets currencies={profile?.currencies} />
        </Section>
      </div>

      <Section title="Bonus buy and ante" description="Cost as a multiple of the bet and the resulting price over the EUR bet range; click for the ladder.">
        <BonusBuys bonusBuy={profile?.bonus_buy} />
      </Section>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <Section title="Paytable" className="xl:col-span-2">
          <Paytable profile={profile} />
        </Section>
        <div className="space-y-4">
          <Section title="Classification"><Classification c={data.classification} /></Section>
          <Section title="Similar games" description="Shared classification attributes.">
            <Similar similar={data.similar} />
          </Section>
        </div>
      </div>

      <Section title="Screenshots" description={data.documentation ? `${data.documentation.pages} documentation pages captured${data.documentation.used_icon ? ` · opened with ${data.documentation.used_icon}` : ''}` : ''}>
        <ShotGallery slug={game.slug} brand={activeBrand} images={data.images || []} />
      </Section>

      <External facts={profile?.external} />

      <Section title="Catalog and capture">
        <CatalogAndTech game={game} profile={profile} />
      </Section>
    </div>
  );
}
